import type { TranslationKey } from '../../i18n/he';
import { ORE_CHAR, ORE_INITIAL_AMOUNT, terrainFromChar } from '../terrain';
import type { TerrainId } from '../terrain';

/*
 * פורמט מפה.
 *
 * המפה נכתבת כשורות טקסט ולא כמערך מספרים, כדי שאפשר יהיה לקרוא אותה בעין,
 * לערוך אותה ידנית, ולראות ב-diff מה בדיוק השתנה. עורך המפות העתידי בדפדפן
 * יפלוט בדיוק את אותו פורמט.
 */

export interface StartSlot {
    readonly x: number;
    readonly y: number;
}

/** סיבוב רבע עם כיוון השעון על מפה ריבועית בגודל n. */
export function rotateQuarterCW(x: number, y: number, n: number): StartSlot {
    return { x: n - 1 - y, y: x };
}

/**
 * בכמה רבעי סיבוב (עם כיוון השעון) צריך לסובב את פריסת הפתיחה של שחקן 0 כדי
 * לקבל את זו של שחקן `index`. מחזירה ‎-1 כשאין זווית כזאת בכלל.
 *
 * **זה המספר שהמנוע צריך, והוא הכללה של `isMirrored` ולא תחליף לו.**
 * ‏`isMirrored` ב-`sim/init.ts` מחזיר "כן/לא" — כלומר 0° או 180° — וזה מספיק
 * לשני שחקנים בדיוק כי הם תמיד בשני קצוות של אותו קוטר. לארבעה שחקנים הוא לא
 * מספיק ואף מזיק: שתי משבצות פתיחה בפינות של האלכסון הנגדי מקבלות את אותו סכום
 * `x + y`, שתיהן נחשבות לא-מסובבות, ולכן שני שחקנים מקבלים פריסה **זהה** במקום
 * מסובבת — המפעל של אחד פונה ליריב ושל השני לפינה (מלכודות 21 ו-44).
 *
 * הפונקציה הזאת מגדירה את הסיבוב לפי **מסלול הסיבוב של משבצת 0** ולא לפי אזור
 * במפה, וזה בכוונה: חלוקה לרבעים לפי צירים או לפי אלכסונים נשברת ברגע שמשבצת
 * פתיחה יושבת על קו החלוקה — וב-`goldbasin`, שנקודות הפתיחה בה על ציר אופקי,
 * שתיהן היו נופלות בדיוק שם. לפי מסלול הסיבוב, כל שש המפות לשני שחקנים מחזירות
 * ‏0 ו-2 (כלומר 0° ו-180°) — בדיוק מה ש-`isMirrored` מחזיר היום.
 */
/**
 * הצורה המינימלית שהפונקציה באמת צריכה — **ולא `MapDef` מלא.**
 *
 * מאז שנקודות הפתיחה מוגרלות בכל משחק (`sim/starts.ts`), הן חיות במצב המשחק
 * ולא בהגדרת המפה, ואותה שאלה בדיוק נשאלת עליהן. טיפוס מבני מרשה לשאול אותה
 * בלי לזייף `MapDef` שאין לו שדות אחרים.
 */
export interface RotatableStarts {
    readonly w: number;
    readonly h: number;
    readonly startSlots: readonly StartSlot[];
}

export function startRotation(def: RotatableStarts, index: number): number {
    const first = def.startSlots[0];
    const slot = def.startSlots[index];
    if (!first || !slot || def.w !== def.h) return -1;

    let point: StartSlot = first;
    for (let quarters = 0; quarters < 4; quarters++) {
        if (point.x === slot.x && point.y === slot.y) return quarters;
        point = rotateQuarterCW(point.x, point.y, def.w);
    }
    return -1;
}

export interface MapDef {
    readonly id: string;
    readonly nameKey: TranslationKey;
    readonly w: number;
    readonly h: number;
    readonly maxPlayers: number;
    /** מיקומי הפתיחה, לפי סדר משבצות השחקנים. */
    readonly startSlots: readonly StartSlot[];
    /** h שורות באורך w תווים כל אחת. */
    readonly rows: readonly string[];
}

/** מפה מפוענחת — זה מה שנכנס למצב המשחק ולרנדרר. */
export interface ParsedMap {
    readonly def: MapDef;
    readonly w: number;
    readonly h: number;
    /** אינדקס סוג קרקע לכל אריח, בסדר שורות. */
    readonly terrain: Uint8Array;
    /** כמות עפרות לכל אריח. 0 = אין. */
    readonly ore: Uint16Array;
}

export function tileIndex(map: { readonly w: number }, x: number, y: number): number {
    return y * map.w + x;
}

export function parseMap(def: MapDef): ParsedMap {
    if (def.rows.length !== def.h) {
        throw new Error(`מפה ${def.id}: ${def.rows.length} שורות במקום ${def.h}`);
    }

    const terrain = new Uint8Array(def.w * def.h);
    const ore = new Uint16Array(def.w * def.h);

    for (let y = 0; y < def.h; y++) {
        const row = def.rows[y] ?? '';
        if (row.length !== def.w) {
            throw new Error(`מפה ${def.id}: שורה ${y} באורך ${row.length} במקום ${def.w}`);
        }
        for (let x = 0; x < def.w; x++) {
            const char = row[x] ?? '';
            const id: TerrainId | undefined = terrainFromChar(char);
            if (id === undefined) {
                throw new Error(`מפה ${def.id}: תו לא מוכר '${char}' ב-(${x},${y})`);
            }
            const i = y * def.w + x;
            terrain[i] = id;
            if (char === ORE_CHAR) ore[i] = ORE_INITIAL_AMOUNT;
        }
    }

    return { def, w: def.w, h: def.h, terrain, ore };
}
